import React, { useEffect } from "react";

import { getSessionDetails } from "../../utils/userSession";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useReactiveState } from "../../utils/stateManagement";
import {
    UserSessionListState,
    deleteSelectedSessions,
    fetchData,
    UserSessionListDialog,
    RenderUserSessionList,
} from "./common";
import { FormattedMessage } from "react-intl";
import { useFetch } from "../FetchProvider";
import { tokenResourceCreatedEventName } from "../../constants/Constants";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

const UserSessionTable: React.FC<{ userSub: string }> = ({ userSub }) => {
    const state = useReactiveState<UserSessionListState>({
        rows: [],
        sessionDialogData: null,
        showDialog: false,
    });
    const fetch = useFetch();

    const onTokenResourceCreated = () => _fetchData();

    useEffect(() => {
        _fetchData().then();

        document.addEventListener(tokenResourceCreatedEventName, onTokenResourceCreated);

        return () => {
            document.removeEventListener(tokenResourceCreatedEventName, onTokenResourceCreated);
        };
    }, []);

    const Label = ({ name }: { name: string }) => (
        <span>
            <FormattedMessage id={`user_session.${name}`} />
        </span>
    );

    const unknown = <Label name="unknown" />;

    const _fetchData = () => fetchData(userSub, fetch).then((rows) => state.setRows(rows));

    const _deleteSelectedSessions = async () => {
        await deleteSelectedSessions([state.sessionDialogData["id"]], fetch);
        state.setShowDialog(false);
        await _fetchData();
    };

    const _showSessionDialog = (data: any) => {
        state.setSessionDialogData(data);
        state.setShowDialog(true);
    };

    const Header = () => (
        <TableHead>
            <TableRow>
                <TableCell>
                    <Label name="type" />
                </TableCell>
                <TableCell>
                    <Label name="device" />
                </TableCell>
                <TableCell>
                    <Label name="os" />
                </TableCell>
                <TableCell>
                    <Label name="client" />
                </TableCell>
                <TableCell>
                    <Label name="last_use" />
                </TableCell>
                <TableCell>
                    <Label name="apps" />
                </TableCell>
            </TableRow>
        </TableHead>
    );

    const Row = (session: any) => {
        const { lastUse } = getSessionDetails(session);
        const userAgent = session["user_agent"];
        let lastUseComp = <span>{lastUse}</span>;

        if (session["current"])
            lastUseComp = (
                <span>
                    <FontAwesomeIcon icon={faCheckCircle} color="#0a0" style={{ marginRight: 4 }} />
                    <Label name="active" />
                </span>
            );

        return (
            <TableRow onClick={() => _showSessionDialog(session)}>
                <TableCell>{session["type"]}</TableCell>
                <TableCell>{userAgent ? userAgent["device"] : unknown}</TableCell>
                <TableCell>{userAgent ? userAgent["os"]["name"] : unknown}</TableCell>
                <TableCell>{userAgent ? userAgent["client"]["name"] : unknown}</TableCell>
                <TableCell>{lastUseComp}</TableCell>
                <TableCell>{session["apps"].map((app: any) => app["name"]).join(", ")}</TableCell>
            </TableRow>
        );
    };

    const Dialog = () => (
        <UserSessionListDialog
            open={state.showDialog}
            data={state.sessionDialogData}
            hide={() => state.setShowDialog(false)}
            deleteSelectedSessions={_deleteSelectedSessions}
            disableDeleteCurrentSession={false}
        />
    );

    const List = () => {
        return (
            <Table>
                <Header />
                <tbody>{...state.rows.map((s) => Row(s))}</tbody>
            </Table>
        );
    };

    return <RenderUserSessionList Dialog={Dialog} List={List} rowsLength={state.rows.length} userSub={userSub} />;
};

export default UserSessionTable;
